import { Box } from '@factorialco/deprecated-design-system'
import {
  DetailsItem,
  DetailsItemsList,
  ResourceHeader,
} from '@factorialco/f0-react/dist/experimental'
import { IntegrationsJoinPremiumPostingItemFragment } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getDatesRange,
  getPremiumPostingStatusColor,
  getPremiumPostingStatusLabel,
} from 'modules/ats/lib/utils/premiumPortals'
import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

interface Props {
  posting: IntegrationsJoinPremiumPostingItemFragment
  onClose: () => void
}

export const PremiumPostingSidepanel = ({ posting, onClose }: Props) => {
  const { formatPrice } = usePricingUtils()

  const order = posting.integrationJoinOrder

  return (
    <Sidepanel
      title={i18n.t('ats.job_details.premium_portals.sidepanel.title')}
      closeLabel={i18n.t('common.close')}
      onClose={onClose}
      overlay
      boxPadding
      open
      headerBorder
      locked
      width="wide"
    >
      <Box gap="s24">
        <ResourceHeader
          title={posting.jobBoard.name + ' - ' + posting.product.name}
          status={{
            label: getPremiumPostingStatusLabel(posting.status),
            text: getPremiumPostingStatusLabel(posting.status),
            variant: getPremiumPostingStatusColor(posting.status),
          }}
          metadata={[
            {
              label: i18n.t('ats.job_details.premium_portals.sidepanel.requested_at'),
              value: { type: 'text', content: dateBackendDateTimeToUI(order.createdAt) },
            },
          ]}
        />
        <DetailsItemsList
          tableView
          details={[
            {
              title: i18n.t('ats.purchase_requests_page.common.requested_by'),
              content: {
                type: 'person',
                firstName: order.requestedBy.firstName,
                lastName: order.requestedBy.lastName,
                avatarUrl: buildUrl(order.requestedBy.avatar) ?? undefined,
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.portal'),
              content: {
                type: 'person',
                firstName: posting.jobBoard.name,
                lastName: '',
                avatarUrl: posting.jobBoard.logoUrl,
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.product'),
              content: {
                type: 'item',
                text: posting.product.name,
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.type'),
              content: {
                type: 'item',
                text:
                  order.premiumOfferType == 'bundle'
                    ? order.premiumOfferName
                    : i18n.t('ats.job_details.premium_portals.sidepanel.individual_product'),
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.price'),
              content: {
                type: 'item',
                text:
                  formatPrice(order.premiumOfferPrice, order.snapshot.currency) +
                  ' ' +
                  (order.premiumOfferType == 'bundle'
                    ? i18n.t('ats.job_details.premium_portals.sidepanel.for_bundle')
                    : ''),
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.duration'),
              content: {
                type: 'item',
                text: i18n.t('ats.job_details.marketplace.common.duration_days', {
                  duration: posting.product.durationDays,
                }),
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.date_range'),
              content: {
                type: 'item',
                text: getDatesRange(posting.fromDate, posting.toDate),
              },
            },
            {
              title: i18n.t('ats.job_details.premium_portals.sidepanel.posting_url'),
              content: {
                type: 'item',
                text: posting.url ?? '',
                action: posting.url
                  ? {
                      type: 'navigate',
                      href: posting.url ?? '',
                    }
                  : undefined,
              },
            },
          ]}
        />
        <DetailsItem
          content={{
            type: 'item',
            text: posting.product.description,
          }}
          title={i18n.t('ats.job_details.premium_portals.sidepanel.product_description')}
        />
      </Box>
    </Sidepanel>
  )
}
